import {Avatar, DatePicker, Input, Radio} from 'antd'
import EmailLookupField from 'components/EmailLookupField'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik, FormikConsumer} from 'formik'
import {parseDayjs} from 'helpers/datetimeHelpers'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useMemo} from 'react'
import {IoCalendarOutline} from 'react-icons/io5'
import {compose, mapProps, pure} from 'recompose'
import {GenderTypes} from 'views/AppLocals/appFieldTypes'
import {gConfigs} from 'views/AppLocals/configs'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {NullAsDefault, renderIf, shallowDiff} from 'views/Shared'
import Pure from '../../components/Pure'
import Translate from '../../modules/local/Translate'
import {appClassNames} from '../AppLocals/custom/appClassNames'
import {HeaderWithIconLine} from "../AppLocals/Templates/items/ListHeader";
import getAvatar from "../../helpers/getAvatar";
import getAliasName from "../../helpers/getAliasName";
import getTitle from "../../helpers/getTitle";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../helpers/getLinkToDetail";
import {BsPersonFill} from "react-icons/bs";
import {FaLink} from "react-icons/fa6";
import dayjs from "dayjs";
import {ActionModalWrapper} from "../../components/form/ActionModalWrapper";
import FormActionBar from "../../components/form/FormActionBar";
import {roleIds} from "../../modules/permissions/contants";
import {
    digitLength,
    emailFormat,
    firstLetterUppercase,
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces,
    noNumber,
    noSpecialChars,
    required,
    runRules
} from "../../components/form/ValidateForm";

const member_modes = {
    organization: 'organization',
    others: 'others',
}


export const formSchema = {
    contact_email: {
        title: 'email',
        children: [
            {
                name: 'contact_email',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, form, translate, onChange}) => ({
                        name,
                        onChange,
                        type: 'email',
                        auto: false,
                        placeholder: translate('ex: email'),
                        defaultValue: value,
                        handleApply: (user) => {
                            const {values, setValues} = form ?? {}
                            const {email, name, date_of_birth, gender, phone_number} = user ?? {}
                            const user_info = _.omitBy(
                                {
                                    email,
                                    name,
                                    gender,
                                    phone_number,
                                    date_of_birth: parseDayjs(date_of_birth),
                                    user_id: user?.id,
                                    contact_email: email ?? undefined,
                                    user,
                                },
                                _.isUndefined
                            )
                            setValues({
                                ...values,
                                ...(user_info ?? {}),
                            })
                        },
                    }))
                )(EmailLookupField),
            },
        ],
    },
    name: {
        title: 'full name',
        children: [
            {
                name: 'name',
                component: createControlledFormField({
                    placeholder: 'name',
                    showCount: true,
                    maxLength: 120,
                }),
            },
        ],
    },
    contact_phone: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                label: 'phone',
                name: 'contact_phone',
                component: createControlledFormField({
                    type: 'tel',
                    placeholder: 'ex: phone',
                    Component: Input,
                }),
            },
        ],
    },
    date_of_birth__gender: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                label: 'date of birth',
                name: 'date_of_birth',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        className: 'w-full',
                        format: gConfigs.dateFormat,
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: `${translate('date of birth')} "DD/MM/YYYY"`,
                        // 🔹 Giới hạn ngày
                        disabledDate: (current) => {
                            const maxDate = dayjs().subtract(4, 'year');
                            const minDate = dayjs().subtract(120, 'year');

                            return (
                                current &&
                                (current > maxDate.endOf('day') || current < minDate.startOf('day'))
                            );
                        },
                        // 🔹 mở lịch ở khoảng năm hợp lý
                        defaultPickerValue: dayjs().subtract(4, 'year'),
                        onChange: (date, dateString) => {
                            onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                        },
                    }))
                )(DatePicker),
            },
            {
                label: 'gender',
                name: 'gender',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        onChange,
                        buttonStyle: 'solid',
                        className: 'w-full custom-radio-button',
                        children: Object.values(GenderTypes).map(
                            ({name, value, label}) => (
                                <Radio.Button
                                    key={name}
                                    value={value}>
                                    {translate(label)}
                                </Radio.Button>
                            )
                        ),
                    }))
                )(Radio.Group),
            },
        ],
    },
    member_groups: {
        title: 'member type',
        children: [
            {
                name: 'member_groups',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <SelectWidget
                                mode="tags"
                                name={name}
                                value={value}
                                allowClear={true}
                                className="w-full"
                                onChange={(value) => {
                                    onChange(createValue(name, value))
                                }}
                                placeholder={`${t('teacher')}; ${t('student')} ... (${t('grading levels placeholder')})`}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
    role_id: {
        title: 'role',
        children: [
            {
                name: 'role_id',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, translate, onChange, form}) => {
                        let _roleCodes = {
                            3: 'member',
                        }
                        if (!!form?.values?.available_actions?.switch_to_owner && !!form?.values?.available_actions?.set_role_admin) {
                            _roleCodes = {
                                2: 'admin',
                                3: 'member',
                            }
                        }
                        return {
                            name,
                            defaultValue: value,
                            allowClear: true,
                            className: 'w-full',
                            placeholder: translate('click to select'),
                            options: Object.keys(_roleCodes).map((e, i) => {
                                return {
                                    key: i,
                                    value: Number(e),
                                    label: translate(roleIds[e]),
                                    disabled: !!value && !!form?.values?.role_id && form?.values?.role_id === Number(e),
                                }
                            }),
                            onChange: (value) => {
                                onChange(createValue(name, value))
                            },
                        }
                    }),
                    pure
                )(SelectWidget),
            },
        ],
    },
}

const validateEmail = (email, t) => {
    const rules = [
        required(t),
        emailFormat(t)
    ].filter(Boolean)
    const message = runRules(email, rules)
    if (message) return { contact_email: message }
}

const validatePhoneNumber = (phoneNumber, t) => {
    if(!phoneNumber) return null;
    const rules = [
        digitLength(10, 16, t)
    ].filter(Boolean)
    const message = runRules(phoneNumber, rules)
    if (message) return { contact_phone: message }
}

const validateName = (name, t) => {
    const rules = [
        required(t),
        lengthBetween(2, 120, t),
        noNumber(t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
        firstLetterUppercase(t)
    ].filter(Boolean)
    const message = runRules(name, rules)
    if (message) return { name: message }
}

const validate = (t) => (values) => {
    const {
        contact_email,
        name,
        contact_phone,
        date_of_birth
    } = values ?? {}
    const errorContactEmail = validateEmail(contact_email, t);
    const errorName = validateName(name, t)
    const errorPhone = validatePhoneNumber(contact_phone, t)
    return {
        ...errorContactEmail,
        ...errorName,
        ...errorPhone
    }
}

export const MemberCreateFrom = ({
                                     owner_id,
                                     owner_type,
                                     isSubmitting,
                                     onCancel = NullAsDefault,
                                     onSubmit = NullAsDefault,
                                 }) => {
    const t = useTranslate()

    const initialValues = useMemo(
        () => ({
            owner_id,
            owner_type,
            member_mode: member_modes.organization,
            gender: 3
        }),
        [owner_id, owner_type]
    )

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                let params = {}
                const {
                    email,
                    user_id,
                    owner_id,
                    owner_type,
                    member_mode,
                    member_groups,
                    ...rest
                } = values ?? {}
                switch (member_mode) {
                    case member_modes.organization:
                        break
                    case member_modes.others:
                        params = {
                            member_groups: Array.from(member_groups ?? []).join(','),
                        }
                        break
                    default:
                        break
                }
                onSubmit({
                    ...rest,
                    ...params,
                    user_id,
                })
            }}>
            <React.Fragment>
                <ActionModalWrapper
                    action={
                        <FormActionGroups
                            isSubmitting={isSubmitting}
                            submitProps={{label: 'invite'}}
                            resetProps={{hidden: true}}
                        />
                    }>
                    <FormikConsumer>
                        {({values, setValues}) => {
                            const {member_mode, user} = values ?? {}

                            return (
                                <React.Fragment>
                                    <Radio.Group
                                        name="member_mode"
                                        value={member_mode}
                                        onChange={(event) => {
                                            const {name, value} = event.target ?? {}
                                            setValues({
                                                ...values,
                                                [name]: value,
                                            })
                                        }}
                                        className="custom-radio-button border-b py-2"
                                        options={[
                                            {
                                                label: t('user of kristo'),
                                                value: member_modes.organization,
                                            },
                                            // {label: t('others'), value: member_modes.others},
                                        ]}
                                    />
                                    {
                                        !!user?.name && (
                                            <Pure>
                                                <div className="flex flex flex-col gap-1">
                                                    <HeaderWithIconLine
                                                        icon={<FaLink size={14} className="text-color-500 mr-1"/>}
                                                        title="link account"
                                                        className="text-base text-color-300"
                                                    />
                                                    <div className="py-2 flex items-center gap-3">
                                                        <Avatar
                                                            size={36}
                                                            src={getAvatar(user?.owner)}
                                                            icon={<BsPersonFill size={18} className="text-color-500"/>}
                                                            className="flex flex-center background-200"
                                                        />
                                                        <div className="flex flex-col">
                                                <span className="font-bold text-color-000">
                                                  {renderIf(
                                                      getAliasName(user?.owner, 'patron_saint'),
                                                      <span
                                                          className="mr-1 font-medium text-sm text-color-400 italic capitalize">
                                                          {getAliasName(user?.owner, 'patron_saint') || ''}
                                                      </span>
                                                  )}
                                                    {getTitle(user)}
                                                </span>
                                                            <span
                                                                className="text-xs text-color-500 italic leading-tight">
                                                    {`${t('account name')} : `}
                                                                <Link target="_blank" to={getLinkToDetail(user)}
                                                                      className="font-medium text-primary-500 no-underline">
                                                        {getTitle(user)}
                                                    </Link>
                                                </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Pure>
                                        )}
                                    <Pure>
                                        <FieldsFactory formSchema={[formSchema.contact_email]}/>
                                    </Pure>
                                    {/*<Pure input={[member_mode]}>*/}
                                    {/*    {*/}
                                    {/*        member_mode === member_modes.others && (*/}
                                    {/*            <FieldsFactory*/}
                                    {/*                formSchema={[*/}
                                    {/*                    formSchema.name,*/}
                                    {/*                    formSchema.date_of_birth__gender,*/}
                                    {/*                    formSchema.contact_phone,*/}
                                    {/*                ]}*/}
                                    {/*            />*/}
                                    {/*        )}*/}
                                    {/*</Pure>*/}
                                </React.Fragment>
                            )
                        }}
                    </FormikConsumer>
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

export const MemberEditFrom = ({
                                   item,
                                   owner_id,
                                   owner_type,
                                   isSubmitting,
                                   onCancel = NullAsDefault,
                                   onSubmit = NullAsDefault,
                               }) => {
    const t = useTranslate()

    const initialValues = useMemo(() => {
        const {user} = item ?? {}
        const _get = (name) => _.get(item, name, _.get(user, name))
        return {
            ...item,
            name: item?.name || _.get(user, 'name'),
            gender: _get('gender'),
            contact_email: _get('email'),
            contact_phone: _get('phone_number'),
            date_of_birth: parseDayjs(_get('date_of_birth')),
        }
    }, [item])

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, initialValues)
                const {
                    name,
                    email,
                    gender,
                    owner_id,
                    owner_type,
                    contact_email,
                    contact_phone,
                    ...rest
                } = changes ?? {}
                onSubmit({
                    name,
                    gender,
                    contact_email,
                    contact_phone,
                })
            }}>
            <ActionModalWrapper
                action={
                    <FormActionGroups
                        isSubmitting={isSubmitting}
                        submitProps={{label: 'save'}}
                    />
                }>
                <FormikConsumer>
                    {({values, setValues}) => {
                        const {user} = values ?? {}
                        return (
                            <React.Fragment>
                                {
                                    !!user?.name && (
                                        <Pure>
                                            <div className="flex flex flex-col gap-1">
                                                <HeaderWithIconLine
                                                    icon={<FaLink size={14} className="text-color-500 mr-1"/>}
                                                    title="link account"
                                                    className="text-base text-color-300"
                                                />
                                                <div className="py-2 flex items-center gap-3">
                                                    <Avatar
                                                        size={36}
                                                        src={getAvatar(user?.owner)}
                                                        icon={<BsPersonFill size={18} className="text-color-500"/>}
                                                        className="flex flex-center background-200"
                                                    />
                                                    <div className="flex flex-col">
                                                <span className="font-bold text-color-000">
                                                  {renderIf(
                                                      getAliasName(user?.owner, 'patron_saint'),
                                                      <span
                                                          className="mr-1 font-medium text-sm text-color-400 italic capitalize">
                                                          {getAliasName(user?.owner, 'patron_saint') || ''}
                                                      </span>
                                                  )}
                                                    {getTitle(user)}
                                                </span>
                                                        <span className="text-xs text-color-500 italic leading-tight">
                                                    {`${t('account name')} : `}
                                                            <Link target="_blank" to={getLinkToDetail(user)}
                                                                  className="font-medium text-primary-500 no-underline">
                                                        {getTitle(user)}
                                                    </Link>
                                                </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Pure>
                                    )
                                }
                                <Pure>
                                    <FieldsFactory
                                        formSchema={[
                                            formSchema.contact_email,
                                            formSchema.name,
                                        ]}
                                    />
                                </Pure>
                            </React.Fragment>
                        )
                    }}
                </FormikConsumer>
            </ActionModalWrapper>
        </Formik>
    )
}


export const MemberRoleAssignFrom = ({
                                         item,
                                         onCancel = NullAsDefault,
                                         onSubmit = NullAsDefault,
                                     }) => {
    const t = useTranslate()

    const initialValues = useMemo(() => {
        return {
            ...item,
        }
    }, [item])

    const validate = (values) => {
        let errors = {}
        const {role_id} = values ?? {}
        if (!!!role_id) {
            errors.role_id = t('required field')
        }
        return errors
    }

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, initialValues)
                const {
                    role_id,
                    ...rest
                } = changes ?? {}
                onSubmit({
                    role_id,
                })
            }}>
            <div className="w-full flex flex-col gap-6">
                <Pure>
                    <div className="flex flex-col gap-3">
                        <FieldsFactory
                            formSchema={[
                                formSchema.role_id
                            ]}
                        />
                    </div>
                </Pure>
                <FormActionBar submitText="save" hideResetButton={true}/>
            </div>
        </Formik>
    )
}